import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MapPin, BedDouble, Bath, Maximize, Calendar, Heart, ChevronLeft, ChevronRight, FileText, Edit2, Lock } from 'lucide-react';
import { formatPrice } from '../utils/formatters';
import { useLeaseStore } from '../stores/leaseStore';
import { useAuth } from '../context/AuthContext';
import { useBillingStore } from '../stores/billingStore';

interface Location {
  lat: number;
  lng: number;
}

interface Listing {
  id: string;
  title: string;
  description: string;
  location: string;
  displayLocation: string;
  coordinates: Location;
  price: number;
  beds: number;
  baths: number;
  sqft: number;
  available: string;
  endDate: string;
  images: string[];
  amenities: string[];
  ownerId: string;
}

interface ListingCardProps {
  listing: Listing;
  onFavorite: () => void;
  isFavorite: boolean;
}

const ListingCard: React.FC<ListingCardProps> = ({ listing, onFavorite, isFavorite }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const { user } = useAuth();
  const { subscription } = useBillingStore();
  const { hasUserApplied } = useLeaseStore();
  const navigate = useNavigate();

  const isOwner = user?.id === listing.ownerId;
  const hasApplied = user && !isOwner ? hasUserApplied(user.id, listing.id) : false;
  const hasPremiumAccess = isOwner || (user && subscription?.status === 'active');

  const nextImage = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex((prev) => 
      prev === listing.images.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex((prev) => 
      prev === 0 ? listing.images.length - 1 : prev - 1
    );
  };

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onFavorite();
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/edit-listing/${listing.id}`);
  };

  const handleCardClick = () => {
    navigate(`/listings/${listing.id}`);
  };

  return (
    <div 
      onClick={handleCardClick}
      className="cursor-pointer group"
    >
      <div className="flex flex-col h-full bg-white rounded-xl overflow-hidden hover:shadow-md transition-shadow">
        {/* Image Section */}
        <div className="relative aspect-[4/3]">
          <div className="absolute inset-0 bg-gray-100">
            <img
              src={listing.images[currentImageIndex]}
              alt={listing.title}
              className="w-full h-full object-cover transition-opacity duration-300"
            />
          </div>

          {/* Premium Badge */}
          {!hasPremiumAccess && (
            <div className="absolute top-3 left-3 bg-black/75 text-white px-3 py-1 rounded-full text-sm font-medium backdrop-blur-sm flex items-center">
              <Lock className="h-4 w-4 mr-1.5" />
              Premium
            </div>
          )}

          {/* Image Navigation */}
          {listing.images.length > 1 && (
            <>
              <button
                onClick={prevImage}
                className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 bg-black/50 hover:bg-black/70 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur-sm"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 bg-black/50 hover:bg-black/70 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur-sm"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </>
          )}

          {/* Image Indicators */}
          {listing.images.length > 1 && (
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex space-x-1.5">
              {listing.images.map((_, index) => (
                <div
                  key={index}
                  className={`w-1.5 h-1.5 rounded-full transition-colors ${
                    index === currentImageIndex 
                      ? 'bg-white' 
                      : 'bg-white/50'
                  }`}
                />
              ))}
            </div>
          )}

          {/* Price Badge */}
          <div className="absolute top-3 right-3">
            <div className="bg-black/75 text-white px-3 py-1 rounded-full text-sm font-medium backdrop-blur-sm">
              {formatPrice(listing.price)}<span className="text-white/80">/mo</span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="absolute top-3 right-3 flex items-center space-x-2">
            {isOwner ? (
              <button
                onClick={handleEditClick}
                className="p-2 bg-white/90 hover:bg-white text-gray-700 rounded-full shadow-sm transition-colors"
              >
                <Edit2 className="h-5 w-5" />
              </button>
            ) : (
              !hasApplied && (
                <button
                  onClick={handleFavoriteClick}
                  className={`p-2 rounded-full shadow-sm transition-colors ${
                    isFavorite 
                      ? 'bg-rose-500 text-white' 
                      : 'bg-white/90 hover:bg-white text-gray-600'
                  }`}
                >
                  <Heart className={isFavorite ? 'h-5 w-5 fill-current' : 'h-5 w-5'} />
                </button>
              )
            )}
          </div>

          {/* Applied Badge */}
          {hasApplied && (
            <div className="absolute bottom-3 left-3 bg-indigo-600 text-white px-3 py-1 rounded-full text-sm font-medium backdrop-blur-sm flex items-center">
              <FileText className="h-4 w-4 mr-1.5" />
              Applied
            </div>
          )}
        </div>

        {/* Content Section */}
        <div className="p-4">
          <h3 className="text-lg font-medium text-gray-900 group-hover:text-gray-700 mb-2">
            {listing.title}
          </h3>

          <div className="flex items-center text-gray-500 text-sm mb-3">
            <MapPin className="h-4 w-4 mr-1 flex-shrink-0" />
            <span>{listing.displayLocation || listing.location}</span>
          </div>

          <div className="flex items-center gap-4 text-gray-500 text-sm">
            <div className="flex items-center">
              <BedDouble className="h-4 w-4 mr-1" />
              <span>{listing.beds === 0 ? 'Studio' : `${listing.beds} Bed`}</span>
            </div>
            <div className="flex items-center">
              <Bath className="h-4 w-4 mr-1" />
              <span>{listing.baths} Bath</span>
            </div>
            <div className="flex items-center">
              <Maximize className="h-4 w-4 mr-1" />
              <span>{listing.sqft} sqft</span>
            </div>
          </div>

          <div className="mt-3 pt-3 border-t border-gray-100 text-sm text-gray-500">
            <div className="flex items-center">
              <Calendar className="h-4 w-4 mr-1" />
              <span>
                Available {new Date(listing.available).toLocaleDateString()} - {new Date(listing.endDate).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(ListingCard);